import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Chip,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  ArrowBack,
  Image as ImageIcon,
  Person,
  Badge as BadgeIcon,
  Verified,
} from '@mui/icons-material';
import { verificationService } from '@/services/verificationService';
import toast from 'react-hot-toast';

export const VerificationPhotoReviewPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [photoData, setPhotoData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: 'approve' | 'reject' | null;
  }>({ open: false, action: null });
  const [imagePreview, setImagePreview] = useState<{
    open: boolean;
    url: string;
    title: string;
  }>({ open: false, url: '', title: '' });

  useEffect(() => {
    if (userId) {
      loadPhotoData();
    }
  }, [userId]);

  const loadPhotoData = async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await verificationService.getUserVerificationPhotos(userId);

      if (result.success && result.data) {
        setPhotoData(result.data);
      } else {
        console.error('❌ Failed to load photos:', result.error);
        setError(result.error || 'Failed to load verification photos');
      }
    } catch (err) {
      console.error('❌ Error loading photos:', err);
      setError('An error occurred while loading verification photos');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (action: 'approve' | 'reject') => {
    if (!userId) return;

    if (action === 'reject' && !notes.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setSubmitting(true);

    try {
      const adminUserId = localStorage.getItem('userId') || 'admin-default';
      const result = await verificationService.reviewVerificationPhotos(
        userId,
        action,
        notes,
        adminUserId
      );

      if (result.success) {
        toast.success(result.message || `Photos ${action}ed successfully`);
        navigate('/verification/overview');
      } else {
        toast.error(result.error || `Failed to ${action} photos`);
      }
    } catch (err) {
      console.error(`Error ${action}ing photos:`, err);
      toast.error(`An error occurred while ${action}ing photos`);
    } finally {
      setSubmitting(false);
      setConfirmDialog({ open: false, action: null });
    }
  };

  const openImagePreview = (url: string, title: string) => {
    setImagePreview({ open: true, url, title });
  };

  const closeImagePreview = () => {
    setImagePreview({ open: false, url: '', title: '' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/verification/overview')}
        >
          Back to Verification List
        </Button>
      </Box>
    );
  }

  if (!photoData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No verification photos found for this user.</Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/verification/overview')}
          sx={{ mt: 2 }}
        >
          Back to Verification List
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Button
            variant="text"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/verification/overview')}
            sx={{ mb: 1 }}
          >
            Back to Verification List
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Verification Photo Review
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Review and approve user verification photos
          </Typography>
        </Box>
        <Chip
          label={photoData.status === 'pending_review' ? 'Pending Review' : photoData.status}
          color={
            photoData.status === 'approved' ? 'success' :
            photoData.status === 'rejected' ? 'error' : 'warning'
          }
          icon={photoData.status === 'approved' ? <CheckCircle /> : undefined}
        />
      </Box>

      {/* User Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person /> User Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Name</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {photoData.userName || photoData.userInfo?.firstName && photoData.userInfo?.lastName
                  ? `${photoData.userInfo.firstName} ${photoData.userInfo.lastName}`
                  : 'Unknown User'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Uploaded At</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {photoData.uploadedAt
                  ? new Date(photoData.uploadedAt).toLocaleString()
                  : 'Invalid Date'}
              </Typography>
            </Grid>
          </Grid>

          {/* Liveness Check */}
          {photoData.livenessCheck && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Verified /> Liveness Check
              </Typography>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip
                    size="small"
                    label={photoData.livenessCheck.passed ? 'Passed' : 'Failed'}
                    color={photoData.livenessCheck.passed ? 'success' : 'error'}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Score</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {(photoData.livenessCheck.score * 100).toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Photos */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon /> Verification Photos
          </Typography>

          <Grid container spacing={3}>
            {/* Document Photo */}
            {(photoData.documentPhotoUrl || photoData.documentImageUrl) && (
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-4px)'
                    }
                  }}
                  onClick={() => openImagePreview(photoData.documentPhotoUrl || photoData.documentImageUrl, 'Document Photo')}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Document Photo
                  </Typography>
                  <Box
                    component="img"
                    src={photoData.documentPhotoUrl || photoData.documentImageUrl}
                    alt="Document"
                    onError={(e: any) => {
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                    }}
                    sx={{
                      width: '100%',
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Click to enlarge
                  </Typography>
                </Paper>
              </Grid>
            )}

            {/* Selfie Photo */}
            {(photoData.selfiePhotoUrl || photoData.selfieImageUrl) && (
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-4px)'
                    }
                  }}
                  onClick={() => openImagePreview(photoData.selfiePhotoUrl || photoData.selfieImageUrl, 'Selfie Photo')}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Selfie Photo
                  </Typography>
                  <Box
                    component="img"
                    src={photoData.selfiePhotoUrl || photoData.selfieImageUrl}
                    alt="Selfie"
                    onError={(e: any) => {
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                    }}
                    sx={{
                      width: '100%',
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Click to enlarge
                  </Typography>
                </Paper>
              </Grid>
            )}

            {/* Identity Photo - from NIN/International Passport */}
            {(photoData.identityPhotoUrl || photoData.ninPhotoUrl || photoData.passportPhotoUrl) && (
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-4px)'
                    }
                  }}
                  onClick={() => openImagePreview(
                    photoData.identityPhotoUrl || photoData.ninPhotoUrl || photoData.passportPhotoUrl,
                    'Identity Photo'
                  )}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Identity Photo
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    {photoData.ninPhotoUrl ? 'NIN' : photoData.passportPhotoUrl ? 'Passport' : 'ID Document'}
                  </Typography>
                  <Box
                    component="img"
                    src={photoData.identityPhotoUrl || photoData.ninPhotoUrl || photoData.passportPhotoUrl}
                    alt="Identity"
                    onError={(e: any) => {
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                    }}
                    sx={{
                      width: '100%',
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Click to enlarge
                  </Typography>
                </Paper>
              </Grid>
            )}

            {/* Show message if no photos available */}
            {!photoData.documentPhotoUrl && !photoData.documentImageUrl &&
             !photoData.selfiePhotoUrl && !photoData.selfieImageUrl &&
             !photoData.identityPhotoUrl && !photoData.ninPhotoUrl && !photoData.passportPhotoUrl && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  No verification photos found. The user may not have uploaded photos yet.
                </Alert>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Review Notes */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Review Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Add notes about your review (required for rejection)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            variant="outlined"
          />
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {photoData.status === 'pending_review' && (
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="error"
            size="large"
            startIcon={<Cancel />}
            onClick={() => setConfirmDialog({ open: true, action: 'reject' })}
            disabled={submitting}
          >
            Reject Photos
          </Button>
          <Button
            variant="contained"
            color="success"
            size="large"
            startIcon={<CheckCircle />}
            onClick={() => setConfirmDialog({ open: true, action: 'approve' })}
            disabled={submitting}
          >
            Approve Photos
          </Button>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => !submitting && setConfirmDialog({ open: false, action: null })}
      >
        <DialogTitle>
          {confirmDialog.action === 'approve' ? 'Approve Photos?' : 'Reject Photos?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.action === 'approve'
              ? 'Are you sure you want to approve these verification photos?'
              : 'Are you sure you want to reject these verification photos? The user will be notified.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, action: null })}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirmDialog.action === 'approve' ? 'success' : 'error'}
            onClick={() => confirmDialog.action && handleReview(confirmDialog.action)}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreview.open}
        onClose={closeImagePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{imagePreview.title}</DialogTitle>
        <DialogContent>
          <Box
            component="img"
            src={imagePreview.url}
            alt={imagePreview.title}
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: '70vh',
              objectFit: 'contain'
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeImagePreview}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VerificationPhotoReviewPage;
